import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withModifiers as _withModifiers, createTextVNode as _createTextVNode, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = { class: "manage-collaborators" }
const _hoisted_2 = { class: "manage-collaborators__subtitle" }
const _hoisted_3 = ["id"]
const _hoisted_4 = { class: "manage-collaborators__selection" }
const _hoisted_5 = { class: "actions" }
const _hoisted_6 = {
  key: 0,
  class: "actions__public-link"
}
const _hoisted_7 = { class: "actions__slot" }

import { computed, ref, onMounted, watch, defineAsyncComponent } from 'vue';
import { useClipboard } from '@vueuse/core';

import Magnify from 'vue-material-design-icons/Magnify.vue';
import Close from 'vue-material-design-icons/Close.vue';
import Check from 'vue-material-design-icons/Check.vue';
import ContentCopy from 'vue-material-design-icons/ContentCopy.vue';
import AccountGroup from 'vue-material-design-icons/AccountGroup.vue';
import Earth from 'vue-material-design-icons/Earth.vue';
import XLoadingIcon from '@components/XLoadingIcon.vue';

import axios from '@nextcloud/axios';
import { showError } from '@services/utils/dialog';
import { generateOcsUrl, generateUrl } from '@nextcloud/router';

import NcButton from '@nextcloud/vue/components/NcButton';
import NcEmptyContent from '@nextcloud/vue/components/NcEmptyContent';
import NcPopover from '@nextcloud/vue/components/NcPopover';
import NcTextField from '@nextcloud/vue/components/NcTextField';
import { t } from '@services/l10n';
import { isAbortError, useAbort } from '@services/utils/abort-vue';
import * as dav from '@services/dav';
import * as utils from '@services/utils/common';
import * as nativex from '@native';

import { ShareType } from '@nextcloud/sharing';

type Collaborator = {
  id: string;
  label: string;
  type: ShareType;
};


export default /*@__PURE__*/_defineComponent({
  ...{
  name: 'AlbumCollaborators',
},
  __name: 'AlbumCollaborators',
  props: {
    albumName: {},
    collaborators: {},
    allowPublicLink: { type: Boolean }
  },
  setup(__props: any, { expose: __expose }) {

const NcListItemIcon = defineAsyncComponent(() => import('@nextcloud/vue/components/NcListItemIcon'));



const props = __props;



const searchText = ref('');
const showPopover = ref(false);
const availableCollaborators = ref({} as { [key: string]: Collaborator });
const selectedCollaboratorsKeys = ref<string[]>([]);
const currentSearchResults = ref<Collaborator[]>([]);
const loadingAlbum = ref(false);
const errorFetchingAlbum = ref<number | null>(null);
const loadingCollaborators = ref(false);
const errorFetchingCollaborators = ref(null);
const randomId = Math.random().toString().slice(2, 10);
const { copy: webCopy, copied: publicLinkCopied } = useClipboard({ copiedDuring: 2000, legacy: true });
const searchAbort = useAbort();
const albumAbort = useAbort();
const config = {
  minSearchStringLength: parseInt(window.OC.config['sharing.minSearchStringLength'], 10) || 0,
};

const searchResults = computed(() =>
  currentSearchResults.value
    .filter(({ id }) => id !== utils.uid)
    .map(({ type, id }) => `${type}:${id}`)
    .filter((collaboratorKey) => !selectedCollaboratorsKeys.value.includes(collaboratorKey)),
);

const listableSelectedCollaboratorsKeys = computed(() =>
  selectedCollaboratorsKeys.value.filter(
    (collaboratorKey) => availableCollaborators.value[collaboratorKey].type !== ShareType.Link,
  ),
);

const selectedCollaborators = computed(() =>
  selectedCollaboratorsKeys.value.map((collaboratorKey) => availableCollaborators.value[collaboratorKey]),
);

const isPublicLinkSelected = computed(() => selectedCollaboratorsKeys.value.includes(`${ShareType.Link}`));
const publicLink = computed(() => availableCollaborators.value[ShareType.Link]);

watch(
  () => props.collaborators,
  (collaborators) => {
    populateCollaborators(collaborators);
  },
);

onMounted(() => {
  searchCollaborators();
  populateCollaborators(props.collaborators);
});

/** Fetch possible collaborators. */
async function searchCollaborators() {
  if (searchText.value.length >= 1) {
    showPopover.value = true;
  }

  const signal = searchAbort.renew();
  try {
    if (searchText.value.length < config.minSearchStringLength) {
      return;
    }

    loadingCollaborators.value = true;
    const response = await axios.get(generateOcsUrl('core/autocomplete/get'), {
      params: {
        search: searchText.value,
        itemType: 'share-recipients',
        shareTypes: [ShareType.User, ShareType.Group],
      },
      signal,
    });
    signal.throwIfAborted();

    currentSearchResults.value = response.data.ocs.data.map((collaborator: any) => {
      switch (collaborator.source) {
        case 'users':
          return {
            id: collaborator.id,
            label: collaborator.label,
            type: ShareType.User,
          };
        case 'groups':
          return {
            id: collaborator.id,
            label: collaborator.label,
            type: ShareType.Group,
          };
        default:
          throw new Error(`Invalid collaborator source ${collaborator.source}`);
      }
    });

    availableCollaborators.value = {
      ...availableCollaborators.value,
      ...currentSearchResults.value.reduce(indexCollaborators, {}),
    };
  } catch (error: any) {
    if (isAbortError(error)) return;
    errorFetchingCollaborators.value = error;
    showError(t('memories', 'Failed to fetch collaborators list.'));
  } finally {
    if (!signal.aborted) loadingCollaborators.value = false;
  }
}

/** Populate selectedCollaboratorsKeys and availableCollaborators. */
function populateCollaborators(collaborators: Collaborator[]) {
  const initialCollaborators = collaborators.reduce(indexCollaborators, {});
  selectedCollaboratorsKeys.value = Object.keys(initialCollaborators);
  availableCollaborators.value = {
    3: {
      id: '',
      label: t('memories', 'Public link'),
      type: ShareType.Link,
    },
    ...availableCollaborators.value,
    ...initialCollaborators,
  };
}

function indexCollaborators(collaborators: { [s: string]: Collaborator }, collaborator: Collaborator) {
  return {
    ...collaborators,
    [`${collaborator.type}${collaborator.type === ShareType.Link ? '' : ':'}${
      collaborator.type === ShareType.Link ? '' : collaborator.id
    }`]: collaborator,
  };
}

async function createPublicLinkForAlbum() {
  if (loadingAlbum.value) return;

  // Check if link already exists
  if (isPublicLinkSelected.value) {
    return await copyPublicLink();
  }

  const signal = albumAbort.renew();
  // Create new link
  selectEntity(`${ShareType.Link}`);
  if (!(await updateAlbumCollaborators(undefined, signal))) {
    unselectEntity(`${ShareType.Link}`);
    return;
  }
  try {
    loadingAlbum.value = true;
    errorFetchingAlbum.value = null;

    if (!utils.uid) return;
    const album = await dav.getAlbum(utils.uid, props.albumName, { signal });
    signal.throwIfAborted();
    populateCollaborators(album.collaborators);
    await copyPublicLink();
  } catch (error: any) {
    if (isAbortError(error)) return;
    if (error.response?.status === 404) {
      errorFetchingAlbum.value = 404;
    } else {
      errorFetchingAlbum.value = error;
    }

    showError(t('memories', 'Failed to fetch album.'));
  } finally {
    if (!signal.aborted) loadingAlbum.value = false;
  }
}

async function deletePublicLink() {
  if (loadingAlbum.value) return;
  const signal = albumAbort.renew();
  const collaborators = selectedCollaborators.value.filter((c) => c.type !== ShareType.Link);
  if (!(await updateAlbumCollaborators(collaborators, signal))) return;

  unselectEntity(`${ShareType.Link}`);
  availableCollaborators.value[3] = {
    id: '',
    label: t('memories', 'Public link'),
    type: ShareType.Link,
  };
}

async function updateAlbumCollaborators(collaborators?: Collaborator[], signal?: AbortSignal): Promise<boolean> {
  collaborators ??= selectedCollaborators.value;
  signal ??= albumAbort.signal;
  try {
    if (!utils.uid) return false;
    loadingAlbum.value = true;
    const album = await dav.getAlbum(utils.uid, props.albumName, { signal });
    signal.throwIfAborted();
    await dav.updateAlbum(
      album,
      {
        albumName: props.albumName,
        properties: {
          collaborators,
        },
      },
      { signal },
    );
    signal.throwIfAborted();
    return true;
  } catch (error) {
    if (isAbortError(error)) return false;
    showError(t('memories', 'Failed to update album.'));
    return false;
  } finally {
    if (!signal.aborted) loadingAlbum.value = false;
  }
}

async function copyPublicLink() {
  const url = generateUrl(`apps/memories/a/${publicLink.value.id}`);
  const link = `${location.origin}${url}`;
  if (nativex.has()) {
    return await nativex.shareUrl(link);
  }

  try {
    await webCopy(link);
  } catch (e) {
    if (isAbortError(e)) return;
    showError(t('memories', 'Failed to copy link to clipboard'));
  }
}

function selectEntity(collaboratorKey: string) {
  if (selectedCollaboratorsKeys.value.includes(collaboratorKey)) return;
  selectedCollaboratorsKeys.value.push(collaboratorKey);
  showPopover.value = false;
}

function unselectEntity(collaboratorKey: string) {
  const index = selectedCollaboratorsKeys.value.indexOf(collaboratorKey);

  if (index === -1) {
    return;
  }

  selectedCollaboratorsKeys.value.splice(index, 1);
}

__expose({ createPublicLinkForAlbum });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(t)('memories', 'Add people or groups who can edit your album')), 1),
    _createElementVNode("form", {
      class: "manage-collaborators__form",
      onSubmit: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"]))
    }, [
      _createVNode(_unref(NcPopover), {
        shown: showPopover.value,
        "auto-size": true,
        distance: 0,
        "focus-trap": false
      }, {
        trigger: _withCtx(({ attrs }) => [
          _createElementVNode("label", _mergeProps({ class: "manage-collaborators__form__input" }, attrs), [
            _createVNode(_unref(NcTextField), {
              modelValue: searchText.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchText).value = $event)),
              autocomplete: "off",
              type: "search",
              name: "search",
              "aria-autocomplete": "list",
              label: _unref(t)('memories', 'Search for collaborators'),
              "aria-label": _unref(t)('memories', 'Search for collaborators'),
              "aria-controls": `manage-collaborators__form__selection-${_unref(randomId)} manage-collaborators__form__list-${_unref(randomId)}`,
              placeholder: _unref(t)('memories', 'Search people or groups'),
              onInput: searchCollaborators
            }, {
              default: _withCtx(() => [
                _createVNode(Magnify, { size: 16 })
              ]),
              _: 1
            }, 8, ["modelValue", "label", "aria-label", "aria-controls", "placeholder"]),
            (loadingCollaborators.value)
              ? (_openBlock(), _createBlock(XLoadingIcon, { key: 0 }))
              : _createCommentVNode("", true)
          ], 16)
        ]),
        default: _withCtx(() => [
          (searchResults.value.length !== 0)
            ? (_openBlock(), _createElementBlock("ul", {
                key: 0,
                id: `manage-collaborators__form__list-${_unref(randomId)}`,
                class: "manage-collaborators__form__list"
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(searchResults.value, (collaboratorKey) => {
                  return (_openBlock(), _createElementBlock("li", { key: collaboratorKey }, [
                    _createVNode(_unref(NcListItemIcon), {
                      id: availableCollaborators.value[collaboratorKey].id,
                      class: "manage-collaborators__form__list__result",
                      name: availableCollaborators.value[collaboratorKey].label,
                      search: searchText.value,
                      user: availableCollaborators.value[collaboratorKey].id,
                      "display-name": availableCollaborators.value[collaboratorKey].label,
                      "aria-label": 
                _unref(t)('memories', 'Add {collaboratorLabel} to the collaborators list', {
                  collaboratorLabel: availableCollaborators.value[collaboratorKey].label,
                })
              ,
                      onClick: ($event: any) => (selectEntity(collaboratorKey))
                    }, null, 8, ["id", "name", "search", "user", "display-name", "aria-label", "onClick"])
                  ]))
                }), 128))
              ], 8, _hoisted_3))
            : (_openBlock(), _createBlock(_unref(NcEmptyContent), {
                key: "emptycontent",
                class: "manage-collaborators__form__list--empty",
                name: _unref(t)('memories', 'No collaborators available')
              }, {
                icon: _withCtx(() => [
                  _createVNode(AccountGroup)
                ]),
                _: 1
              }, 8, ["name"]))
        ]),
        _: 1
      }, 8, ["shown"])
    ], 32),
    _createElementVNode("ul", _hoisted_4, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(listableSelectedCollaboratorsKeys.value, (collaboratorKey) => {
        return (_openBlock(), _createElementBlock("li", {
          key: collaboratorKey,
          class: "manage-collaborators__selection__item"
        }, [
          _createVNode(_unref(NcListItemIcon), {
            id: availableCollaborators.value[collaboratorKey].id,
            "display-name": availableCollaborators.value[collaboratorKey].label,
            name: availableCollaborators.value[collaboratorKey].label,
            user: availableCollaborators.value[collaboratorKey].id
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(NcButton), {
                variant: "tertiary",
                "aria-label": 
              _unref(t)('memories', 'Remove {collaboratorLabel} from the collaborators list', {
                collaboratorLabel: availableCollaborators.value[collaboratorKey].label,
              })
            ,
                onClick: ($event: any) => (unselectEntity(collaboratorKey))
              }, {
                icon: _withCtx(() => [
                  _createVNode(Close, { size: 20 })
                ]),
                _: 1
              }, 8, ["aria-label", "onClick"])
            ]),
            _: 2
          }, 1032, ["id", "display-name", "name", "user"])
        ]))
      }), 128))
    ]),
    _createElementVNode("div", _hoisted_5, [
      (__props.allowPublicLink)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            (isPublicLinkSelected.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createVNode(_unref(NcButton), {
                    class: "manage-collaborators__public-link-button",
                    "aria-label": _unref(t)('memories', 'Copy the public link'),
                    disabled: publicLink.value.id === '' || loadingAlbum.value,
                    onClick: copyPublicLink
                  }, {
                    icon: _withCtx(() => [
                      (_unref(publicLinkCopied))
                        ? (_openBlock(), _createBlock(Check, { key: 0 }))
                        : (_openBlock(), _createBlock(ContentCopy, { key: 1 }))
                    ]),
                    default: _withCtx(() => [
                      (_unref(publicLinkCopied))
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createTextVNode(_toDisplayString(_unref(t)('memories', 'Public link copied!')), 1)
                          ], 64))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _createTextVNode(_toDisplayString(_unref(t)('memories', 'Copy public link')), 1)
                          ], 64))
                    ]),
                    _: 1
                  }, 8, ["aria-label", "disabled"]),
                  _createVNode(_unref(NcButton), {
                    variant: "tertiary",
                    "aria-label": _unref(t)('memories', 'Delete the public link'),
                    disabled: publicLink.value.id === '' || loadingAlbum.value,
                    onClick: deletePublicLink
                  }, {
                    icon: _withCtx(() => [
                      (publicLink.value.id === '')
                        ? (_openBlock(), _createBlock(XLoadingIcon, { key: 0 }))
                        : (_openBlock(), _createBlock(Close, { key: 1 }))
                    ]),
                    _: 1
                  }, 8, ["aria-label", "disabled"])
                ], 64))
              : (_openBlock(), _createBlock(_unref(NcButton), {
                  key: 1,
                  class: "manage-collaborators__public-link-button",
                  disabled: loadingAlbum.value,
                  onClick: createPublicLinkForAlbum
                }, {
                  icon: _withCtx(() => [
                    _createVNode(Earth)
                  ]),
                  default: _withCtx(() => [
                    _createTextVNode(" " + _toDisplayString(_unref(t)('memories', 'Share via public link')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"]))
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_7, [
        _renderSlot(_ctx.$slots, "default", { collaborators: selectedCollaborators.value })
      ])
    ])
  ]))
}
}

})